import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as actions from "../actions";
import * as selectors from "../selectors";
import "./DriversConnectionsGame.css";
import LoadingScreen from '../../common/components/LoadingScreen';
import MinigameTutorial from "../../common/components/MinigameTutorial"; // nuevo componente compartido
import { sourceImages } from "../../../helpers/sourceMiniGamesImages"; // ya lo usas en MinigamesHome
import { tutorialTexts } from "../../../helpers/minigameTutorialTexts"; // explicaciones por minijuego
import CooldownScreen from "../../cooldown/components/CooldownScreen";
import { getCooldownForGame } from "../../cooldown/selectors";
import { fetchCooldown } from "../../cooldown/actions";
import { getUser } from "../../users/selectors";

const categoryColors = [
  "#1e88e5", // Blue
  "#43a047", // Green
  "#fb8c00", // Orange
  "#8e24aa", // Purple
  "#d81b60", // Pink
  "#fdd835"  // Yellow
];

const DriversConnectionsGame = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const game = useSelector(selectors.getConnectionsGame);
  const isValidGroup = useSelector(selectors.getValidationResult);

  const [selectedDrivers, setSelectedDrivers] = useState([]);
  const [solvedGroups, setSolvedGroups] = useState([]);
  const [lastValidatedGroup, setLastValidatedGroup] = useState(null);
  const [partialMatchCount, setPartialMatchCount] = useState(null);
  const [retainSelection, setRetainSelection] = useState(false);

  const { canPlay, secondsRemaining, loading } = useSelector(state =>
    getCooldownForGame(state, "DriversConnections")
  );

  const user = useSelector(getUser);

  const translations = {
    es: {
      title: "Drivers Connection",
      loading: "Cargando juego...",
      validate: "Validar Grupo",
      surrender: "Rendirse",
      backToHome: "🏁 Volver al inicio",
      correctGroup: "✅ ¡Grupo correcto!",
      incorrectGroup: "❌ Grupo incorrecto",
      correctMatches: "Coincidencias correctas"
    },
    en: {
      title: "Drivers Connection",
      loading: "Loading game...",
      validate: "Validate Group",
      surrender: "Give Up",
      backToHome: "🏁 Back to home",
      correctGroup: "✅ Correct group!",
      incorrectGroup: "❌ Incorrect group",
      correctMatches: "Correct matches"
    }
  };

  const lang = navigator.language.startsWith("es") ? "es" : "en";
  const t = translations[lang];

  const [showTutorial, setShowTutorial] = useState(true);
  const tutorial = tutorialTexts["/minigames/driversConnections"][lang];

  useEffect(() => {
    dispatch(fetchCooldown("DriversConnections"));
  }, [dispatch]);

  useEffect(() => {
    if (canPlay) {
      dispatch(actions.startConnectionsGame());
    }
  }, [canPlay, dispatch]);

  const toggleDriver = (name) => {
    if (selectedDrivers.includes(name)) {
      setSelectedDrivers(prev => prev.filter(n => n !== name));
    } else {
      if (selectedDrivers.length < 4) {
        setSelectedDrivers(prev => [...prev, name]);
      }
    }
  };

  const handleValidate = () => {
    if (selectedDrivers.length === 4) {
      // Limpia estado visual y asegura que el mensaje se controle por retainSelection
      setPartialMatchCount(null);
      setRetainSelection(true);

      setLastValidatedGroup([...selectedDrivers]);
      dispatch(actions.validateGroup({
        gameId: game.id,
        selectedDriverNames: selectedDrivers
      }));
    }
  };

  const handleSurrender = () => {
    dispatch(actions.revealAnswers(game.id));
  };

  const handleGoHome = () => {
    navigate("/minigames");
  };

  // === EFECTO DE RESPUESTA DE VALIDACIÓN (usa lastValidatedGroup, NO selectedDrivers.length) ===
  useEffect(() => {
    if (
      lastValidatedGroup &&
      Array.isArray(lastValidatedGroup) &&
      lastValidatedGroup.length === 4
    ) {
      if (isValidGroup === true) {
        const solved = game.categories.find(cat => {
          const pilotNames = cat.pilots.map(p => p.driverName);
          return lastValidatedGroup.every(d => pilotNames.includes(d));
        });
        if (solved && !solvedGroups.some(cat => cat.code === solved.code)) {
          setSolvedGroups(prev => [...prev, solved]);
        }
        setSelectedDrivers([]);
        setLastValidatedGroup(null);
        setPartialMatchCount(null);
        setRetainSelection(false);
      } else if (isValidGroup === false) {
        const bestMatch = game.categories.reduce((max, cat) => {
          const pilotNames = cat.pilots.map(p => p.driverName);
          const matches = lastValidatedGroup.filter(d => pilotNames.includes(d)).length;
          return Math.max(max, matches);
        }, 0);
        setPartialMatchCount(bestMatch); // puede ser 0, 1, 2 o 3
        // retainSelection ya está en true desde handleValidate
      }
    }
  }, [isValidGroup, lastValidatedGroup, game, solvedGroups]);

  // Ocultar mensaje a los 3s solo cuando es incorrecto
  useEffect(() => {
    if (retainSelection && isValidGroup === false) {
      const timeout = setTimeout(() => {
        setRetainSelection(false);
      }, 3000);
      return () => clearTimeout(timeout);
    }
  }, [retainSelection, isValidGroup]);

  const allDrivers = useMemo(() => {
    if (!game) return [];
    return game.categories
      .flatMap(c => c.pilots)
      .filter(d => !solvedGroups.some(cat => cat.pilots.some(p => p.driverName === d.driverName)))
      .sort(() => Math.random() - 0.5);
  }, [game, solvedGroups]);

  if (loading) {
    return <LoadingScreen lang={lang} text={t.loading} />;
  }

  if (!canPlay) {
    return (
      <CooldownScreen
        seconds={secondsRemaining}
        onBack={() => navigate("/minigames")}
      />
    );
  }

  if (showTutorial) {
    return (
      <MinigameTutorial
        title={tutorial.title}
        description={tutorial.description}
        image={sourceImages("./DriversConnection.png")}
        onStart={() => setShowTutorial(false)}
        lang={lang}
      />
    );
  }

  if (!game) return <LoadingScreen lang={lang} text={t.loading} />;

  const getColorClass = (index) => `category-color-${index % categoryColors.length}`;
  const gameFinished = game.finished || solvedGroups.length === game.categories.length;

  return (
    <div className="drivers-connections-container">
      <h2 className="connections-title">🏎️ {t.title} 🏎️</h2>

      {!gameFinished && solvedGroups.length > 0 && (
        <div className="solved-grid">
          {solvedGroups.map((category, idx) => (
            <div key={idx} className={`solved-category ${getColorClass(idx)}`}>
              <h3 className="category-title" style={{ color: '#000', fontWeight: 'bold', letterSpacing: '0.5px' }}>
                {category.description}
              </h3>
              <div className="solved-row">
                {category.pilots.map((pilot, i) => (
                  <div key={i} className="solved-slot">
                    {pilot.driverName}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {!gameFinished && (
        <div className="grid-4x4">
          {allDrivers.map((driver) => (
            <div
              key={driver.id}
              className={`grid-cell ${selectedDrivers.includes(driver.driverName) ? 'selected' : ''}`}
              onClick={() => toggleDriver(driver.driverName)}
            >
              {driver.driverName}
            </div>
          ))}
        </div>
      )}

      {gameFinished && (
        <div className="solved-grid">
          {game.categories.map((category, idx) => (
            <div key={idx} className={`solved-category ${getColorClass(idx)}`}>
              <h3 className="category-title" style={{ color: '#000', fontWeight: 'bold', letterSpacing: '0.5px' }}>
                {category.description}
              </h3>
              <div className="solved-row">
                {category.pilots.map((pilot, i) => (
                  <div key={i} className="solved-slot">
                    {pilot.driverName}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="actions-row">
        {!gameFinished ? (
          <>
            <button className="validate-btn" onClick={handleValidate} disabled={selectedDrivers.length !== 4}>
              {t.validate}
            </button>
            <button className="reveal-btn" onClick={handleSurrender}>{t.surrender}</button>
          </>
        ) : (
          <button className="validate-btn" onClick={handleGoHome}>{t.backToHome}</button>
        )}
      </div>

      {isValidGroup === true && (
        <div className="result-msg success">{t.correctGroup}</div>
      )}

      {isValidGroup === false && retainSelection && (
        <div className="result-msg fail">
          {t.incorrectGroup}
          {partialMatchCount !== null && (
            <div style={{ fontSize: "1rem", marginTop: "0.3rem" }}>
              {t.correctMatches}: {partialMatchCount} / 4
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DriversConnectionsGame;
